import { snakeCase } from 'lodash'
import { formatDateParam } from 'helpers/datetime'

export const baseUrl = import.meta.env.VITE_API_URL
export const audience = import.meta.env.VITE_AUDIENCE

// Range and date popovers write only the `{prefix}Min{suffix}` / `...Option` keys,
// never the bare accessor_key, so a column is active when any of these is present.
const columnFilterKeys = (tableColumn) => {
  const key = tableColumn.accessor_key
  const prefix = tableColumn.section_abbreviation?.toLowerCase() || ''
  const suffix = key.slice(prefix.length)
  return [key, `${key}Operator`, `${key}Option`, `${prefix}Min${suffix}`, `${prefix}Max${suffix}`]
}

export const dynamicParams = (params, tableFilters, tableColumns) => {
  return (tableColumns || []).reduce((acc, tableColumn) => {
    if (!tableColumn.accessor_key) return acc
    if (!columnFilterKeys(tableColumn).some((key) => key in params)) return acc

    const f = tableColumn.accessor_key
    const accessor = snakeCase(tableColumn.accessor_key)
    if (f in params) acc[accessor] = tableFilters[f]
    if (tableColumn.filter_type === 'basic') {
      acc[`${accessor}_operator`] = tableFilters[`${f}Operator`]
    }
    if (
      tableColumn.filter_type === 'multiselect' ||
      tableColumn.filter_type === 'mcc_select' ||
      tableColumn.filter_type === 'group_multiselect'
    ) {
      const modeValue = tableFilters[`${f}Mode`]
      if (modeValue && modeValue !== 'include') {
        acc[`${accessor}_mode`] = modeValue
      }
    }
    if (
      tableColumn.filter_type === 'min_max' ||
      tableColumn.filter_type === 'min_max_percent' ||
      tableColumn.filter_type === 'time_in_seconds'
    ) {
      const operatorVal = tableFilters[`${f}Operator`]
      if (operatorVal && operatorVal !== 'range') {
        acc[`${accessor}_operator`] = operatorVal
        const opValue = tableFilters[`${f}OperatorValue`]
        if (opValue !== undefined && opValue !== '') {
          acc[`${accessor}_operator_value`] = opValue
        }
      }
    }
    if (
      tableColumn.filter_type === 'date' ||
      tableColumn.filter_type === 'min_max' ||
      tableColumn.filter_type === 'min_max_percent' ||
      tableColumn.filter_type === 'time_in_seconds'
    ) {
      const prefix = tableColumn.section_abbreviation?.toLowerCase() || ''
      const suffix = tableColumn.accessor_key.slice(prefix.length)
      const minKey = `${prefix}Min${suffix}`
      const maxKey = `${prefix}Max${suffix}`
      const optionKey = `${tableColumn.accessor_key}Option`

      if (tableFilters[minKey] || tableFilters[maxKey]) {
        if (tableColumn.filter_type === 'date') {
          acc[snakeCase(minKey)] = formatDateParam(tableFilters[minKey])
          acc[snakeCase(maxKey)] = formatDateParam(tableFilters[maxKey])
        } else {
          acc[snakeCase(minKey)] = tableFilters[minKey]
          acc[snakeCase(maxKey)] = tableFilters[maxKey]
        }
      }
      if (tableFilters[optionKey] && tableFilters[optionKey] !== 'any') {
        acc[snakeCase(optionKey)] = tableFilters[optionKey]
      }
    }
    return acc
  }, {})
}

export const customColumnParams = (tableFilters, customColumns) => {
  const acc = {}

  // Process all custom columns
  customColumns.forEach(customColumn => {
    const baseKey = `custom_column_${customColumn.id}`

    // Check for the main filter parameter
    if (tableFilters[baseKey] !== undefined && tableFilters[baseKey] !== '') {
      acc[baseKey] = tableFilters[baseKey]
    }

    // Check for min/max parameters for number and date columns
    if (customColumn.column_type === 'number' || customColumn.column_type === 'date') {
      // Use underscore format that matches what the filter popovers set: custom_column_min_{id}
      const minKey = `custom_column_min_${customColumn.id}`
      const maxKey = `custom_column_max_${customColumn.id}`

      if (tableFilters[minKey] !== undefined && tableFilters[minKey] !== '') {
        if (customColumn.column_type === 'date') {
          // Format dates for API
          acc[minKey] = formatDateParam(tableFilters[minKey])
        } else {
          acc[minKey] = tableFilters[minKey]
        }
      }

      if (tableFilters[maxKey] !== undefined && tableFilters[maxKey] !== '') {
        if (customColumn.column_type === 'date') {
          // Format dates for API
          acc[maxKey] = formatDateParam(tableFilters[maxKey])
        } else {
          acc[maxKey] = tableFilters[maxKey]
        }
      }

      // Check for date option parameter (for relative date filters like "Last Month", "Q1", etc.)
      if (customColumn.column_type === 'date') {
        const optionKey = `${baseKey}Option`
        if (tableFilters[optionKey] !== undefined && tableFilters[optionKey] !== 'any' && tableFilters[optionKey] !== '') {
          acc[`${baseKey}_option`] = tableFilters[optionKey]
        }
      }
    }

    // Check for operator parameters for text columns
    if (customColumn.column_type === 'text') {
      const operatorKey = `${baseKey}Operator`
      if (tableFilters[operatorKey] !== undefined && tableFilters[operatorKey] !== '') {
        acc[`${baseKey}_operator`] = tableFilters[operatorKey]
      }
    }
  })

  return acc
}

/**
 * Builds account-related parameters from a params object
 * Handles single account segments and multi-select account segments with operators
 *
 * @param {Object} params - The source params object
 * @returns {Object} Object containing all account-related parameters
 */
export function buildAccountParams(params) {
  return {
    // Single account segment fields
    org_id: params.orgId || '',
    object_id: params.objectId || '',
    seg_one_id: params.segOne?.value || '',
    seg_two_id: params.segTwo?.value || '',
    seg_three_id: params.segThree?.value || '',
    seg_four_id: params.segFour?.value || '',
    seg_five_id: params.segFive?.value || '',

    // Multi-select account segment fields (arrays)
    org_ids: params.orgs?.join('+') || [],
    object_ids: params.objects?.join('+') || [],
    project_ids: params.projects?.join('+') || [],
    seg_one_ids: params.segOnes?.join('+') || [],
    seg_two_ids: params.segTwos?.join('+') || [],
    seg_three_ids: params.segThrees?.join('+') || [],
    seg_four_ids: params.segFours?.join('+') || [],
    seg_five_ids: params.segFives?.join('+') || [],
    seg_six_ids: params.segSixes?.join('+') || [],
    seg_seven_ids: params.segSevens?.join('+') || [],
    seg_eight_ids: params.segEights?.join('+') || [],

    // Operators for multi-select fields
    orgs_operator: params.orgsOperator,
    objects_operator: params.objectsOperator,
    projects_operator: params.projectsOperator,
    seg_ones_operator: params.segOnesOperator,
    seg_twos_operator: params.segTwosOperator,
    seg_threes_operator: params.segThreesOperator,
    seg_fours_operator: params.segFoursOperator,
    seg_fives_operator: params.segFivesOperator,
    seg_sixes_operator: params.segSixesOperator,
    seg_sevens_operator: params.segSevensOperator,
    seg_eights_operator: params.segEightsOperator
  }
}